import {
  ClockInput,
  clockIn,
  addDocument,
  documentFileUrl,
  getDocs,
  findSpots,
  AmountField,
  ConfirmDialog,
  DatePick,
  Dialog,
  getPeople,
  InvalidInput,
  moneyIn,
  parseAmount,
  NumberField,
  lodgings,
  Pick,
  type Booking,
  type BookingInput,
  type Draft,
  type Plan,
  type Spot,
  type WayEnd,
  type Lodging,
  setEnds as saveEnds,
  setTicket,
  TAKE_TEXT,
  type TakenText,
  updateStop,
  uploadDocumentFile,
} from '@trippath/shared';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { iataOf } from '../../lib/airports';
import { linkOf, timeOf, wallOf } from '../../lib/clock';
import {
  changed,
  draftOf,
  inputOf,
  PartMenu,
  TicketEditor,
  useReference,
  withLeg,
  type LegDraft,
  type LegStart,
  type Sector,
  type TicketDraft,
  type TicketZones,
} from './TicketEditor';
import { PdfView } from '../file/PdfView';
import { codeOfFile } from '../../lib/qr';
import { cents, stopInputOf } from '../../lib/stopInput';
import { SpotField } from '../place/SpotField';
import { problemKey } from '../../lib/format';
import { useTownMoney } from '../../lib/townMoney';

interface Props {
  plan: Plan;
  /** The booking as it is, with its stop, when editing; a new one starts at the first stop. */
  booking?: { booking: Booking; stop: string };
  /** A booking read from a typed line, to be checked before it is added. */
  draft?: Draft | undefined;
  /** The PDFs or photos it was read from: attached, and shown beside the form to check it. */
  sources?: File[] | undefined;
  /** A ticket for a stop's way on: the stop and the kind are known, its times asked too. */
  way?: {
    departure: string | null;
    arrival: string | null;
    about: (departure: string) => string | null;
    /** By car: a car rented. */
    rental?: boolean;
    /** By air: its booking number is a PNR. */
    flight?: boolean;
    /** The stops it may go to, the next first: a ticket on through later ones changes there. */
    towards?: { id: string; city: string }[];
  };
  onClose: () => void;
  /** Keeps the booking, and a ticket's times for its way. */
  save: (
    input: BookingInput,
    times: { departure: string | null; arrival: string | null; until?: string | null },
  ) => Promise<Plan>;
  remove?: () => Promise<Plan>;
  onSaved: (plan: Plan) => void;
  /** The leg of the ticket it was opened for: its legs shown first, that one alone. */
  sector?: Sector | null;
}

/** A day moved as far as another moved, from `[was, now]`; as it is without one. */
function shifted(day: string, moved: [string, string | null] | null): string {
  const [was, now] = moved ?? [day, day];
  return now === null ? day : later(day, between(was, now));
}

/** `2026-10-18` and 3 days on: `2026-10-21`. */
function later(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

/** The nights from one day to another. */
function between(from: string, to: string): number {
  return Math.round(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000,
  );
}

/** What the form starts with: the booking being edited, a line read, or a stay at the first stop. */
function seedOf(plan: Plan, booking: Props['booking'], draft: Draft | undefined) {
  if (booking) {
    const { booking: was, stop } = booking;
    // Priced as it was said: in its own money when it has one, else the trip's.
    return {
      ...was,
      stop,
      price: (was.paid ?? was.price).minor,
      currency: (was.paid ?? was.price).currency,
      reference: was.reference ?? '',
    };
  }
  if (draft) {
    // A mail says a stay's hours and a flight's number too.
    return {
      ...draft,
      stop: draft.stop ?? plan.stops[0]?.id ?? null,
      reference: draft.reference ?? '',
      checkIn: draft.checkIn ?? null,
      checkOut: draft.checkOut ?? null,
      flight: draft.flight ? { number: draft.flight, airline: null } : null,
      place: null,
      lodging: null,
      freeUntil: null,
    };
  }
  return {
    stop: plan.stops[0]?.id ?? null,
    kind: 'stay' as const,
    name: '',
    day: null,
    nights: 1,
    price: null,
    currency: null,
    pay: 'paid' as const,
    reference: '',
    link: null,
    checkIn: null,
    checkOut: null,
    flight: null,
    place: null,
    lodging: null,
    freeUntil: null,
  };
}

/** A value put in a field as though typed there, so React hears it. */
function typeInto(input: HTMLInputElement, value: string) {
  Reflect.set(HTMLInputElement.prototype, 'value', value, input);
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * A field as a person reads its name: its label, after the part of the form it is in when there
 * is one — `Departure · Date`.
 */
export function fieldName(field: HTMLElement): string {
  const named = (ids: string | null) =>
    (ids ?? '')
      .split(' ')
      .map((id) => document.getElementById(id)?.textContent ?? '')
      .find((text) => text !== '') ?? '';
  const label =
    field instanceof HTMLInputElement
      ? (field.labels?.[0]?.textContent ?? field.getAttribute('aria-label') ?? '')
      : named(field.getAttribute('aria-labelledby'));
  const part = named(
    field.closest('[role="group"][aria-labelledby]')?.getAttribute('aria-labelledby') ?? null,
  );
  return [part, label.replace('*', '').trim()].filter((text) => text !== '').join(' · ');
}

/** Adds, edits or removes a booking: a stay at a stop, or a ticket for the way on from it. */
export function BookingDialog({
  plan,
  booking,
  draft,
  sources = [],
  way,
  onClose,
  save,
  remove,
  onSaved,
  sector = null,
}: Props) {
  const { t } = useTranslation();
  const nameId = useId();
  const referenceId = useId();
  const ids = useId();
  const first = useRef<HTMLInputElement>(null);
  // The field last chosen in the form, the name at first: a text pressed in the PDF goes there,
  // marked so while the PDF is beside it, and what became of the last text pressed.
  const chosen = useRef<HTMLElement | null>(null);
  const [into, setInto] = useState<string | null>(null);
  const [took, setTook] = useState<
    { text: string; into: string; taken: boolean } | 'lost' | null
  >(null);
  const was = booking?.booking;
  const [seed] = useState(() => seedOf(plan, booking, draft));
  const [stop, setStop] = useState<string | null>(seed.stop);
  const [kind, setKind] = useState<'stay' | 'ticket'>(seed.kind);
  // A ticket goes to its journey's end: on through the changes after it.
  const [until, setUntil] = useState<string | null>(
    (
      way?.towards?.find((s) => plan.stops.find((p) => p.id === s.id)?.transfer !== true) ??
      way?.towards?.[0]
    )?.id ?? null,
  );
  // Its name says where it goes: `Sydney → Abu Dhabi` is `Sydney → Paris` once on to Paris.
  const renamed = (named: string, to: string | null) => {
    const now = way?.towards?.find((s) => s.id === to)?.city;
    const was = way?.towards?.find((s) => named.endsWith(` → ${s.city}`))?.city;
    return now !== undefined && was !== undefined
      ? `${named.slice(0, named.length - was.length)}${now}`
      : named;
  };
  const [name, setName] = useState(() => renamed(seed.name, until));
  const [day, setDay] = useState<string | null>(seed.day);
  const [nights, setNights] = useState<number | null>(seed.nights);
  // A stay's last morning, from its first day and its nights, and its hours.
  const [leaving, setLeaving] = useState<string | null>(
    seed.day === null ? null : later(seed.day, seed.nights),
  );
  const [inAt, setInAt] = useState(seed.checkIn ?? '');
  const [outAt, setOutAt] = useState(seed.checkOut ?? '');
  // Until when a stay is cancelled free: its day and its hour on the stop's clock.
  const [freeDay, setFreeDay] = useState<string | null>(seed.freeUntil?.slice(0, 10) ?? null);
  const [freeAt, setFreeAt] = useState(seed.freeUntil?.slice(11, 16) ?? '');
  const [price, setPrice] = useState<number | null>(seed.price);
  // Priced in the money it is paid in: picked, else a stay's town's and a ticket's trip's.
  const [picked, setPicked] = useState<string | null>(seed.currency);
  const here = plan.stops.find((one) => one.id === stop);
  const town = useTownMoney(
    plan.trip.id,
    here?.currency ?? null,
    plan.trip.currency,
    day ?? here?.start ?? null,
  );
  const currency = picked ?? (kind === 'stay' ? town : plan.trip.currency);
  const [pay, setPay] = useState<'paid' | 'later'>(seed.pay);
  const [reference, setReference] = useState(seed.reference);
  // A ticket's QR code, read from its file or typed, to show from the phone.
  const [qr, setQr] = useState(was?.qr ?? '');
  // The same hand each time: the files are not read again for a new one.
  const readCode = useCallback((code: string) => {
    setQr((typed) => (typed.trim() === '' ? code : typed));
  }, []);
  const [address, setAddress] = useState(seed.link ?? '');
  // A PDF or a photo of it, kept with the trip's documents once the booking is.
  // Its files, an invoice and a boarding pass, and which is shown.
  const [files, setFiles] = useState<File[]>(sources);
  // Those kept with the booking when it was added, shown beside it again; not sent again.
  const [stored, setStored] = useState<File[]>([]);
  useEffect(() => {
    if (was === undefined) return;
    let live = true;
    const trip = plan.trip.id;
    getDocs(trip)
      .then((screen) =>
        Promise.all(
          screen.documents
            .flatMap((one) =>
              one.booking === was.id && one.file ? [{ ...one, type: one.file.type }] : [],
            )
            .map(async (one) => {
              const got = await fetch(documentFileUrl(trip, one.id));
              return new File([await got.blob()], one.name, { type: one.type });
            }),
        ),
      )
      .then(
        (found) => {
          if (live) setStored(found);
        },
        // None to show: the booking is edited without them.
        () => undefined,
      );
    return () => {
      live = false;
    };
  }, [plan.trip.id, was]);
  const all = useMemo(() => [...stored, ...files], [stored, files]);
  const [looked, setLooked] = useState(0);
  const file = all[Math.min(looked, all.length - 1)] ?? null;
  // Each file's QR code looked for, the one shown or not: the first found is the ticket's.
  useEffect(() => {
    const gone = new AbortController();
    void (async () => {
      for (const one of files) {
        const code = await codeOfFile(one);
        if (code !== null) {
          // Closed or its files changed meanwhile: the code is not said.
          if (!gone.signal.aborted) readCode(code);
          return;
        }
      }
    })();
    return () => {
      gone.abort();
    };
  }, [files, readCode]);
  // A PDF or a photo attached is shown beside the form, so what was read is checked against it.
  const preview = useMemo(
    () =>
      file !== null && (file.type === 'application/pdf' || file.type.startsWith('image/'))
        ? URL.createObjectURL(file)
        : null,
    [file],
  );
  useEffect(
    () => () => {
      if (preview !== null) URL.revokeObjectURL(preview);
    },
    [preview],
  );
  const [place, setPlace] = useState<Spot | null>(
    seed.place ? { ...seed.place, detail: '' } : null,
  );
  // What kind of place a stay is: a hotel, a hostel, a ryokan.
  const [lodging, setLodging] = useState<Lodging | null>(seed.lodging);
  // A ticket's legs and people, sent once touched; and which part of it shows.
  const at = plan.stops.findIndex((s) => s.id === seed.stop);
  // A leg to each stop it goes through until `to`, or to its journey's end, past the changes.
  const hops = (to: string | null): LegStart[] => {
    const legs: LegStart[] = [];
    const on = at < 0 ? [] : plan.stops.slice(at);
    for (const [index, from] of on.entries()) {
      const next = on[index + 1];
      if (!next) break;
      const leaves = from.out?.departureAt;
      const lands = from.out?.arrivalAt;
      legs.push({
        origin: iataOf(from.out?.from?.name) ?? from.city,
        destination: iataOf(from.out?.to?.name) ?? next.city,
        day:
          index === 0
            ? seed.day
            : leaves
              ? wallOf(leaves, from.zone).day
              : (from.out?.flight?.day ?? from.start),
        // The ticket's own day moves where its way lands with it.
        arrival: shifted(
          lands ? wallOf(lands, next.zone).day : next.start,
          index === 0 && leaves ? [wallOf(leaves, from.zone).day, seed.day] : null,
        ),
        // Each flight's clocks as its way says them; the first as the ticket says it.
        departureTime:
          index === 0 && way?.departure
            ? way.departure
            : leaves
              ? wallOf(leaves, from.zone).time
              : '',
        arrivalTime: lands ? wallOf(lands, next.zone).time : '',
        // The flight its way was planned on, until the ticket says its own.
        number: (index === 0 ? seed.flight?.number : null) ?? from.out?.flight?.number ?? '',
      });
      if (to === null ? !next.transfer : next.id === to) break;
    }
    return legs.length > 0 ? legs : [{ origin: '', destination: '', day: seed.day }];
  };
  // How the way goes: a ticket by air is in flights, with airlines and aircraft known.
  // How the ticket's file says it goes outweighs how the way was planned: a Flix ticket is a bus.
  const said = was === undefined ? (draft?.ticket?.mode ?? null) : null;
  const planned = plan.stops[at]?.out?.mode ?? null;
  const mode = said ?? planned;
  const { airlines, airports } = useReference(mode === 'flight');
  // A leg's end as a ticket's name says it: the trip's stop of its airport's town, else that
  // town, else as typed — `CDG` is `Paris`.
  const placeName = (end: string) => {
    const airport = airports.find((one) => one.code === end.trim().toUpperCase());
    if (!airport) return end.trim();
    const stop = plan.stops.find(
      (one) => one.city.toLowerCase() === airport.city.toLowerCase(),
    );
    return stop?.city ?? airport.city;
  };
  // A ticket's route by its legs' ends, each once in turn: `Tallinn → Helsinki → Pariis`.
  const routeOf = (legs: TicketDraft['legs']) =>
    legs
      .flatMap((leg, at) => (at === 0 ? [leg.origin, leg.destination] : [leg.destination]))
      .map(placeName)
      .filter((end, at, all) => end !== '' && end !== all[at - 1])
      .join(' → ');
  // Whether a name is only a route — places joined by arrows, each one a stop, a leg's end or
  // its town — and so follows the legs; a name of one's own words never does.
  const isRoute = (named: string, legs: TicketDraft['legs']) => {
    const parts = named.split('→').map((part) => part.trim());
    const known = new Set([
      ...plan.stops.map((one) => one.city),
      ...legs.flatMap((leg) =>
        [leg.origin, leg.destination].flatMap((end) => [end, placeName(end)]),
      ),
    ]);
    return parts.length >= 2 && parts.every((part) => known.has(part));
  };
  // A ticket's legs changed: its name too, while it is only their route.
  const retitle = (next: TicketDraft) => {
    const was = routeOf(ticket.legs);
    const route = routeOf(next.legs);
    setTicketDraft(next);
    setName((named) =>
      route.includes('→') && (named === was || isRoute(named, next.legs)) ? route : named,
    );
  };
  const zones: TicketZones = {
    from: plan.stops[at]?.zone ?? null,
    to: plan.stops[at + 1]?.zone ?? null,
  };
  // When the way here lands, its clock here: a way on leaves after it.
  const comes = plan.stops[at - 1]?.out?.arrivalAt;
  const landed = comes ? wallOf(comes, zones.from) : null;
  // A bus's or a train's ride as its ticket's file says it: its legs, its seat and its traveller,
  // in place of the plan's.
  const read = was === undefined ? (draft?.ticket?.legs ?? []) : [];
  const [ticket, setTicketDraft] = useState<TicketDraft>(() => {
    const made = draftOf(
      was,
      read.length > 0
        ? read.map((ride) => ({
            origin: ride.origin,
            destination: ride.destination,
            day: ride.departure.length > 5 ? ride.departure.slice(0, 10) : seed.day,
            arrival: ride.arrival.length > 5 ? ride.arrival.slice(0, 10) : seed.day,
            departureTime: ride.departure.slice(-5),
            arrivalTime: ride.arrival.slice(-5),
            number: ride.number ?? '',
            // A flight's company is its airline.
            [draft?.ticket?.mode === 'flight' ? 'airline' : 'operator']: ride.operator ?? '',
            vehicle: ride.vehicle ?? '',
            gate: ride.platform ?? '',
          }))
        : hops(until),
      zones,
    );
    const said = read.filter((ride) => ride.seat !== null || ride.passenger !== null);
    return said.length === 0
      ? made
      : {
          ...made,
          passengers: [
            {
              name: said.find((ride) => ride.passenger !== null)?.passenger ?? '',
              traveller: null,
              price: null,
              // Its own number, else the booking's.
              number: read.find((ride) => ride.ticket !== null)?.ticket ?? seed.reference,
              seats: read.map((ride) => ride.seat ?? ''),
              coaches: read.map(() => ''),
              kinds: read.map(() => null),
              bags: null,
            },
          ],
        };
  });
  // What a file said is sent as though typed.
  const [touched, setTouched] = useState(read.length > 0);
  // The stations picked where the ticket starts and ends: the way's own ends once it is kept,
  // the other as it was said; and the towns they are near.
  const [ends, setEnds] = useState<{ from?: Spot; to?: Spot }>({});
  const out = plan.stops[at]?.out;
  const kept = (end: WayEnd | null | undefined) =>
    end?.own === true ? { ...end, detail: '' } : null;
  const own = { from: kept(out?.from), to: kept(out?.to) };
  const towns = {
    from: plan.stops[at]?.place ?? null,
    to: plan.stops[at + 1]?.place ?? null,
  };
  // The stations a ticket's file names, looked for near their towns once: where its way leaves
  // and arrives, a bus station not the airport a flight planned would have.
  const [lookups] = useState(() =>
    [
      { side: 'from' as const, name: read[0]?.origin, near: towns.from },
      { side: 'to' as const, name: read.at(-1)?.destination, near: towns.to },
    ].flatMap(({ side, name, near }) => (name && near ? [{ side, name, near }] : [])),
  );
  const [found, setFound] = useState<{ from?: Spot; to?: Spot }>({});
  useEffect(() => {
    const stop = new AbortController();
    for (const { side, name, near } of lookups) {
      findSpots(name, near, stop.signal).then(
        ({ spots: [best] }) => {
          if (best) setFound((was) => ({ ...was, [side]: { ...best, name } }));
        },
        // Not found: the way's end is worked out as before.
        () => undefined,
      );
    }
    return () => {
      stop.abort();
    };
  }, [lookups]);
  const firstNumber = ticket.legs
    .slice(0, 1)
    .map((l) => l.number.trim())
    .join('');
  const [part, setPart] = useState<'ticket' | 'legs' | 'people' | 'rules'>(
    sector ? 'legs' : 'ticket',
  );
  // The trip's own people, to put on a ticket by name; one alone is on a new ticket already.
  const [members, setMembers] = useState<string[]>([]);
  const asking = part === 'people';
  useEffect(() => {
    if (!asking) return;
    getPeople(plan.trip.id).then(
      (people) => {
        const names = people.members.map((m) => m.name);
        setMembers(names);
        // The owner alone, no one else on the trip: theirs is the ticket.
        const [only] = names;
        if (only !== undefined && names.length === 1 && plan.travellers.length === 0) {
          setTouched(true);
          // A traveller a file said with no name is theirs too.
          setTicketDraft((was) =>
            was.passengers.some((p) => p.name !== '')
              ? was
              : {
                  ...was,
                  passengers:
                    was.passengers.length === 0
                      ? [
                          {
                            name: only,
                            traveller: null,
                            price: null,
                            number: '',
                            seats: [],
                            coaches: [],
                            kinds: [],
                            bags: null,
                          },
                        ]
                      : was.passengers.map((p) => ({ ...p, name: only })),
                },
          );
        }
      },
      // Without them a name is typed.
      () => undefined,
    );
  }, [asking, plan.trip.id, plan.travellers]);
  // What the ticket says of when its first flight goes, said in its flights too.
  const follow = (change: Partial<LegDraft>) => {
    setTicketDraft((was) => ({
      ...was,
      legs: was.legs.map((l, at) => (at === 0 ? changed(l, change) : l)),
    }));
    setTouched(true);
  };
  const [leaves, setLeaves] = useState(way?.departure ?? '');
  const [arrives, setArrives] = useState(way?.arrival ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);
  const [removing, setRemoving] = useState(false);

  // What is wrong, said at each field; a ticket's own fields shown, so none is wrong unseen.
  const shown = (wrong: Record<string, string>) => {
    setErrors(wrong);
    const own = Object.keys(wrong).some((field) => field !== 'ticket');
    // Shown where one is, it stays there; else the part that shows it is opened.
    const here = part === 'ticket' ? own : wrong.ticket !== undefined;
    if (!here && own) setPart('ticket');
  };
  const done = (job: Promise<Plan>) => {
    job.then(onSaved, (error: unknown) => {
      if (error instanceof InvalidInput) {
        // No rate for the money it is in: said at the price.
        shown(
          Object.fromEntries(
            error.fields.map((f) => [
              f.field === 'currency' ? 'price' : f.field,
              t(problemKey(f.problem)),
            ]),
          ),
        );
      } else {
        setFailed(true);
      }
    });
  };

  const submit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const required = t('problem.required');
    const missing: Record<string, string> = {};
    if (stop === null) missing.stop = required;
    if (name.trim() === '') missing.name = required;
    // With its first day a stay ends on a day; without it, it lasts its nights.
    const stayed = day !== null && leaving !== null ? between(day, leaving) : nights;
    if (kind === 'stay' && day === null && nights === null) missing.nights = required;
    if (kind === 'stay' && day !== null && leaving === null) missing.leaving = required;
    const checkIn = kind === 'stay' ? timeOf(inAt) : null;
    const checkOut = kind === 'stay' ? timeOf(outAt) : null;
    const freeTime = kind === 'stay' && freeDay !== null ? timeOf(freeAt) : null;
    if (price === null) missing.price = required;
    const link = linkOf(address);
    const leftAt = timeOf(leaves);
    const arrivedAt = timeOf(arrives);
    const wrong = t('problem.format');
    if (link === undefined) missing.link = wrong;
    if (leftAt === undefined) missing.leaves = wrong;
    if (arrivedAt === undefined) missing.arrives = wrong;
    if (checkIn === undefined) missing.checkIn = wrong;
    if (checkOut === undefined) missing.checkOut = wrong;
    if (freeTime === undefined) missing.freeAt = wrong;
    const details = kind === 'ticket' && touched ? inputOf(ticket, zones, airlines) : null;
    if (details === undefined) missing.ticket = wrong;
    shown(missing);
    setFailed(false);
    const stayNights = kind === 'stay' ? stayed : 0;
    if (
      stop === null ||
      price === null ||
      stayNights === null ||
      link === undefined ||
      leftAt === undefined ||
      arrivedAt === undefined ||
      checkIn === undefined ||
      checkOut === undefined ||
      freeTime === undefined ||
      Object.keys(missing).length > 0
    ) {
      return;
    }
    // The booking's own id: the one edited, or the one the plan has new.
    const idOf = (next: Plan) =>
      was?.id ??
      next.stops
        .flatMap((s) => s.bookings)
        .find((b) => !plan.stops.some((s) => s.bookings.some((o) => o.id === b.id)))?.id;
    // The booking kept, its files go with the trip's documents, at its stop and tied to it, one
    // after another: opened again, it shows them.
    const attach = (next: Plan) =>
      files
        .reduce<Promise<unknown>>(
          (before, one) =>
            before
              .then(() =>
                addDocument(plan.trip.id, {
                  kind: kind === 'ticket' ? 'ticket' : 'booking',
                  name: name.trim(),
                  note: '',
                  validFrom: null,
                  validTo: null,
                  stop,
                  private: false,
                  traveller: null,
                  booking: idOf(next) ?? null,
                }),
              )
              .then((document) => uploadDocumentFile(plan.trip.id, document.id, one)),
          Promise.resolve(),
        )
        .then(() => next);
    // The way on goes as its ticket says, set before the ticket is kept (a way booked keeps its
    // mode) — only at the stop its day found, never at one picked by hand.
    const held = plan.stops.find((s) => s.id === stop);
    const regoes =
      kind === 'ticket' &&
      said !== null &&
      said !== planned &&
      held?.out != null &&
      stop === draft?.stop
        ? updateStop(
            plan.trip.id,
            held.id,
            stopInputOf(held, {
              mode: said,
              way: {
                estimate: cents(held.out.estimate),
                departureAt: held.out.departureAt,
                arrivalAt: held.out.arrivalAt,
                link: held.out.link,
              },
            }),
          )
        : Promise.resolve(plan);
    done(
      regoes.then(() =>
        save(
          {
            stop,
            kind,
            name,
            day,
            nights: stayNights,
            price,
            // In its own money; a ticket's in the trip's unless another is picked, its people's
            // shares counted in the trip's.
            currency: kind === 'stay' || currency !== plan.trip.currency ? currency : null,
            pay,
            reference: reference.trim() === '' ? null : reference,
            link,
            checkIn,
            checkOut,
            // The flight number is the first flight's, said in the ticket's flights; a bus's or a
            // train's line is no flight.
            flight:
              kind === 'ticket' && mode === 'flight' && firstNumber !== '' ? firstNumber : null,
            place:
              kind === 'stay' && place
                ? { lat: place.lat, lon: place.lon, name: place.name }
                : null,
            lodging: kind === 'stay' ? lodging : null,
            // Free until the end of its day unless an hour is said.
            freeUntil:
              freeDay === null || kind !== 'stay' ? null : `${freeDay}T${freeTime ?? '23:59'}`,
            qr: kind === 'ticket' && qr.trim() !== '' ? qr.trim() : null,
          },
          { departure: leftAt, arrival: arrivedAt, until },
        )
          .then(attach)
          // Its legs and people, once the booking is: its id, or the one the plan has new.
          .then((next) => {
            const id = idOf(next);
            return details && id ? setTicket(plan.trip.id, id, details) : next;
          })
          // The way leaves from and arrives at the stations picked, the other end as it was said.
          .then((next) => {
            // Those found by name only at the stop the ticket's day found, never one picked.
            const sure = stop === draft?.stop;
            const from = ends.from ?? (sure ? found.from : undefined);
            const to = ends.to ?? (sure ? found.to : undefined);
            return from === undefined && to === undefined
              ? next
              : saveEnds(plan.trip.id, stop, { from: from ?? own.from, to: to ?? own.to });
          }),
      ),
    );
  };

  const title = was
    ? t('bookingForm.editTitle', { name: was.name })
    : way
      ? t(way.rental === true ? 'bookingForm.rentalTitle' : 'bookingForm.ticketTitle', {
          name: seed.name,
        })
      : // A draft read from a line is checked; one with nothing typed yet is new.
        t(draft && draft.name !== '' ? 'bookingForm.checkTitle' : 'bookingForm.newTitle');
  // When it arrives, about, from when it leaves and how long it takes.
  const leftAt = timeOf(leaves);
  const estimate = way && typeof leftAt === 'string' ? way.about(leftAt) : null;
  const about = typeof estimate === 'string' ? `~${estimate}` : '';
  const time = (
    id: string,
    label: string,
    value: string,
    set: (v: string) => void,
    hint: string,
  ) => (
    <div className="field">
      <label className="field-label" htmlFor={`${ids}-${id}`}>
        {label}
      </label>
      <ClockInput
        id={`${ids}-${id}`}
        className="text-input booking-time"
        data-takes-text="clock"
        placeholder={hint}
        value={value}
        aria-invalid={errors[id] !== undefined}
        onChange={set}
      />
      {errors[id] !== undefined && <p className="field-error">{errors[id]}</p>}
    </div>
  );
  const linked = linkOf(address);
  // A ticket in its parts: itself, its legs, its people, its bags; at its legs and its bags,
  // their menu at the right. Held under the title while the rest scrolls.
  const parts = kind === 'ticket' && (
    <div className="booking-parts-row">
      <div className="segmented booking-parts" role="tablist">
        {(['ticket', 'legs', 'people', 'rules'] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={part === option}
            onClick={() => {
              setPart(option);
            }}
          >
            {t(`ticket.part.${option === 'legs' && mode === 'flight' ? 'flights' : option}`)}
          </button>
        ))}
      </div>
      {part === 'legs' && (
        <PartMenu
          label={t('ticket.legsMenu')}
          add={t(mode === 'flight' ? 'ticket.addFlight' : 'ticket.addLeg')}
          onAdd={() => {
            retitle(withLeg(ticket));
            setTouched(true);
          }}
        />
      )}
      {part === 'rules' && (
        <PartMenu
          label={t('ticket.bagsMenu')}
          add={t('ticket.addBag')}
          onAdd={() => {
            const bags = ticket.rules.checkedBags;
            setTicketDraft({
              ...ticket,
              rules: { ...ticket.rules, checkedBags: [...bags, bags.at(-1) ?? 23] },
            });
            setTouched(true);
          }}
        />
      )}
    </div>
  );

  // The money a price may be in: its own, the trip's, those of the trip's stops.
  const offered = [
    ...new Set([
      currency,
      plan.trip.currency,
      ...plan.stops.map((one) => one.currency).filter((code): code is string => code !== null),
    ]),
  ];

  // A text pressed in the PDF, with the rest of its line: an amount (`12,00 EUR`) becomes the
  // price, or the amount last chosen; a date's field takes the date it says, a clock's its clock;
  // any other text goes into the field last chosen, as though typed there.
  const take = (text: string, line: string) => {
    // The field chosen, the name at first; gone with its part of the form, none.
    const last = chosen.current;
    if (last === null || !last.isConnected) {
      setTook('lost');
      return;
    }
    // Where it went, or that it did not; the field it went into flashes, so the eye finds it.
    const said = (taken: boolean, shown = text, field: HTMLElement = last) => {
      setTook({ text: shown, into: fieldName(field), taken });
      if (!taken) return;
      field.classList.remove('pdf-taken');
      requestAnimationFrame(() => {
        field.classList.add('pdf-taken');
      });
    };
    // An amount with its cents, or with its currency (`€ 18`): the price.
    const money = moneyIn(text, offered);
    const amount =
      /^\D{0,4}(\d[\d\s]*[.,]\d{2})\D{0,4}$/.exec(text)?.[1] ??
      (money?.currency ? money.amount : undefined);
    const price = (into: HTMLInputElement[], value: string) => {
      const other = money?.currency && money.currency !== currency ? money.currency : null;
      if (other !== null && into.every((input) => input.closest('.booking-price'))) {
        // In another currency than the one picked: that currency picked, the price in it.
        setPicked(other);
        setPrice(parseAmount(value, other));
        setTook({ text, into: t('bookingForm.price'), taken: true });
        requestAnimationFrame(() => {
          prices()[0]?.classList.add('pdf-taken');
        });
        return;
      }
      for (const input of into) typeInto(input, value);
      said(true, text, into[0]);
    };
    const prices = () => [
      ...document.querySelectorAll<HTMLInputElement>('.booking-price .amount-input'),
    ];
    if (!(last instanceof HTMLInputElement)) {
      // A date's field: the date the text says, else the one its line says.
      for (const each of [text, line]) {
        const given: TakenText = { text: each, taken: false };
        last.dispatchEvent(new CustomEvent(TAKE_TEXT, { detail: given, bubbles: true }));
        if (given.taken) {
          said(true, each);
          return;
        }
      }
      // No date, but an amount: the price, as from any field.
      if (amount === undefined) said(false);
      else price(prices(), amount);
      return;
    }
    // Into a clock's field its clock alone, the last said, not a date's: `Helsingi L 16.05.2026
    // kell 20.30` is `20:30`.
    const clock = clockIn(text);
    if (last.dataset.takesText === 'clock' && clock !== null) {
      typeInto(last, clock);
      said(true);
      return;
    }
    // Into a code's field the code alone, without what names it: a flight's `EY 451` whole,
    // `Broneerimisnumber 5059246` is `5059246`, `PNR: 92XYRA` is `92XYRA` — the last word with a
    // digit, else the last in capitals.
    const words = text.split(/[\s:#]+/).filter((word) => /^[A-Za-z0-9][\w-]{2,}$/.test(word));
    const code =
      /(?<![\w-])(?=[A-Z0-9]{2}\s?\d)(?:[A-Z]{2}|[A-Z]\d|\d[A-Z])\s?\d{1,4}[A-Z]?(?![\w-])/.exec(
        text,
      )?.[0] ??
      words.findLast((word) => /\d/.test(word)) ??
      words.findLast((word) => /^[A-Z0-9-]+$/.test(word) && word.length >= 5);
    if (last.dataset.takesText === 'code' && code !== undefined) {
      typeInto(last, code);
      said(true);
      return;
    }
    if (amount === undefined) {
      typeInto(last, text);
      said(true);
      return;
    }
    price(last.classList.contains('amount-input') ? [last] : prices(), amount);
  };

  return (
    <>
      <Dialog
        open={!removing}
        onClose={onClose}
        title={title}
        initialFocus={first}
        wide={kind === 'ticket' || preview !== null}
        bar={parts}
      >
        <div className={preview === null ? undefined : 'booking-beside'}>
          <form
            className="form"
            onSubmit={submit}
            noValidate
            onFocus={(event) => {
              const field = event.target;
              if (
                (field instanceof HTMLInputElement && field.type === 'text') ||
                (field instanceof HTMLElement && field.dataset.takesText !== undefined)
              ) {
                // Marked as where a text pressed in the PDF goes.
                delete chosen.current?.dataset.pdfTarget;
                field.dataset.pdfTarget = '';
                chosen.current = field;
                setInto(fieldName(field));
                setTook(null);
              }
            }}
          >
            {Object.keys(errors).length > 0 && (
              <p className="form-failed" role="alert">
                {t('problem.check')}
              </p>
            )}
            {kind === 'ticket' && part !== 'ticket' && (
              <TicketEditor
                draft={ticket}
                part={part}
                price={price}
                currency={plan.trip.currency}
                travellers={plan.travellers}
                members={members}
                mode={mode}
                sector={sector}
                towns={towns}
                stations={{
                  from: ends.from ?? found.from ?? own.from,
                  to: ends.to ?? found.to ?? own.to,
                }}
                onEnd={(side, spot) => {
                  setEnds((was) => ({ ...was, [side]: spot }));
                }}
                onChange={(next) => {
                  retitle(next);
                  setTouched(true);
                  // The ticket says what its first flight says: its day and its clocks.
                  for (const lead of next.legs.slice(0, 1)) {
                    setDay(lead.departureDay);
                    setLeaves(lead.departureTime);
                    setArrives(lead.arrivalTime);
                  }
                }}
              />
            )}
            {errors.ticket !== undefined && <p className="field-error">{errors.ticket}</p>}
            <div
              className="booking-ticket-part"
              hidden={kind === 'ticket' && part !== 'ticket'}
            >
              <div className="field">
                <label className="field-label" htmlFor={nameId} data-required>
                  {t('bookingForm.name')}
                </label>
                <input
                  id={nameId}
                  ref={first}
                  className="text-input"
                  type="text"
                  value={name}
                  aria-invalid={errors.name !== undefined}
                  aria-describedby={errors.name === undefined ? undefined : `${nameId}-error`}
                  onChange={(event) => {
                    setName(event.target.value);
                  }}
                />
                {errors.name !== undefined && (
                  <p className="field-error" id={`${nameId}-error`}>
                    {errors.name}
                  </p>
                )}
              </div>
              {!way && (
                <div className="form-row">
                  <div>
                    <Pick
                      label={t('bookingForm.stop')}
                      required
                      options={plan.stops.map((s) => ({ value: s.id, label: s.city }))}
                      value={stop}
                      onChange={setStop}
                    />
                    {errors.stop !== undefined && <p className="field-error">{errors.stop}</p>}
                  </div>
                  <div className="field">
                    <span className="field-label" id={`${ids}-kind`}>
                      {t('bookingForm.kind')}
                    </span>
                    <div className="segmented" role="group" aria-labelledby={`${ids}-kind`}>
                      {(['stay', 'ticket'] as const).map((option) => (
                        <button
                          key={option}
                          type="button"
                          aria-pressed={kind === option}
                          onClick={() => {
                            setKind(option);
                          }}
                        >
                          {t(`bookingForm.${option}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {kind === 'stay' ? (
                <div className="form-row booking-stay">
                  <div className="booking-when">
                    <DatePick
                      label={t('bookingForm.checkIn')}
                      value={day}
                      onChange={(next) => {
                        setDay(next);
                        // The nights kept: leaving moves with the day of arrival.
                        setLeaving(next === null ? null : later(next, nights ?? 1));
                      }}
                    />
                    {time('checkIn', t('bookingForm.from'), inAt, setInAt, '15:00')}
                  </div>
                  <div className="booking-when">
                    {day === null ? (
                      <NumberField
                        label={t('bookingForm.nights')}
                        required
                        value={nights}
                        onChange={setNights}
                        min={0}
                        max={3650}
                        error={errors.nights}
                      />
                    ) : (
                      <div>
                        <DatePick
                          label={t('bookingForm.checkOut')}
                          required
                          value={leaving}
                          onChange={(next) => {
                            setLeaving(next);
                            if (next !== null) setNights(between(day, next));
                          }}
                          from={day}
                        />
                        {errors.leaving !== undefined && (
                          <p className="field-error">{errors.leaving}</p>
                        )}
                      </div>
                    )}
                    {time('checkOut', t('bookingForm.until'), outAt, setOutAt, '11:00')}
                  </div>
                  <div className="booking-when">
                    <DatePick
                      label={t('bookingForm.freeUntil')}
                      value={freeDay}
                      onChange={setFreeDay}
                    />
                    {time('freeAt', t('bookingForm.freeAt'), freeAt, setFreeAt, '23:59')}
                  </div>
                </div>
              ) : (
                <div className="form-row">
                  <DatePick
                    label={t('bookingForm.departs')}
                    value={day}
                    onChange={(next) => {
                      setDay(next);
                      follow({ departureDay: next });
                    }}
                  />
                  {way?.towards && way.towards.length > 1 && (
                    // One ticket on through later stops: they become changes on the way.
                    <Pick
                      label={t('bookingForm.towards')}
                      options={way.towards.map((s) => ({ value: s.id, label: s.city }))}
                      value={until}
                      searchable={false}
                      onChange={(to) => {
                        setUntil(to);
                        setName(renamed(name, to));
                        // Its legs follow while none has been said.
                        if (!touched) setTicketDraft(draftOf(was, hops(to), zones));
                      }}
                    />
                  )}
                  {way && (
                    <div className="booking-times">
                      {time(
                        'leaves',
                        t('plan.leaves'),
                        leaves,
                        (v) => {
                          setLeaves(v);
                          follow({ departureTime: v });
                        },
                        landed && landed.day === day
                          ? t('bookingForm.after', { time: landed.time })
                          : t('ticket.clockHint'),
                      )}
                      {time(
                        'arrives',
                        t('plan.arrives'),
                        arrives,
                        (v) => {
                          setArrives(v);
                          follow({ arrivalTime: v });
                        },
                        about,
                      )}
                    </div>
                  )}
                </div>
              )}
              {kind === 'stay' && day !== null && leaving !== null && (
                <p className="quiet booking-nights">
                  {t('bookingForm.nightsCount', { count: between(day, leaving) })}
                </p>
              )}
              <div className="form-row booking-price">
                <AmountField
                  key={currency}
                  label={t('bookingForm.price')}
                  required
                  value={price}
                  onChange={setPrice}
                  currency={currency}
                  currencies={offered}
                  onCurrency={(code: string) => {
                    setPicked(code);
                    setPrice(null);
                  }}
                  error={errors.price}
                />
                <Pick
                  label={t('bookingForm.pay')}
                  options={[
                    { value: 'paid', label: t('bookingForm.paid') },
                    {
                      value: 'later',
                      label: t(
                        kind === 'ticket' ? 'bookingForm.laterTicket' : 'bookingForm.later',
                      ),
                    },
                  ]}
                  value={pay}
                  searchable={false}
                  onChange={setPay}
                />
              </div>
              <div className="form-row">
                <div className="field">
                  <label className="field-label" htmlFor={referenceId}>
                    {t(
                      // A PNR is a flight's; a bus's or a train's ticket has a number of its own.
                      kind === 'ticket' && (way?.flight === true || mode === 'flight')
                        ? 'bookingForm.pnr'
                        : 'bookingForm.reference',
                    )}
                  </label>
                  <input
                    id={referenceId}
                    className="text-input"
                    type="text"
                    data-takes-text="code"
                    value={reference}
                    aria-invalid={errors.reference !== undefined}
                    aria-describedby={
                      errors.reference === undefined ? undefined : `${referenceId}-error`
                    }
                    onChange={(event) => {
                      setReference(event.target.value);
                    }}
                  />
                  {errors.reference !== undefined && (
                    <p className="field-error" id={`${referenceId}-error`}>
                      {errors.reference}
                    </p>
                  )}
                </div>
                {kind === 'ticket' && (
                  <div className="field">
                    <label className="field-label" htmlFor={`${ids}-qr`}>
                      {t('ticketCode.field')}
                    </label>
                    <input
                      id={`${ids}-qr`}
                      className="text-input mono"
                      type="text"
                      autoComplete="off"
                      value={qr}
                      onChange={(event) => {
                        setQr(event.target.value);
                      }}
                    />
                    {errors.qr !== undefined && <p className="field-error">{errors.qr}</p>}
                  </div>
                )}
                {errors.flight !== undefined && <p className="field-error">{errors.flight}</p>}
                {kind === 'stay' && (
                  <Pick
                    label={t('bookingForm.lodging')}
                    options={lodgings.map((value) => ({ value, label: t(`lodging.${value}`) }))}
                    value={lodging}
                    placeholder={t('bookingForm.lodgingAny')}
                    searchable={false}
                    onChange={setLodging}
                    onClear={() => {
                      setLodging(null);
                    }}
                  />
                )}
                {kind === 'stay' && (
                  <SpotField
                    label={t('bookingForm.place')}
                    near={plan.stops.find((s) => s.id === stop)?.place ?? null}
                    spot={place}
                    onPick={setPlace}
                    onClear={() => {
                      setPlace(null);
                    }}
                  />
                )}
              </div>
              <div className="field">
                <label className="field-label" htmlFor={`${ids}-link`}>
                  {t('plan.link')}
                </label>
                <div className="stay-hotel-link">
                  <input
                    id={`${ids}-link`}
                    className="text-input"
                    type="text"
                    autoComplete="off"
                    placeholder="https://…"
                    value={address}
                    aria-invalid={errors.link !== undefined}
                    onChange={(event) => {
                      setAddress(event.target.value);
                    }}
                  />
                  {typeof linked === 'string' && (
                    <a className="text-link" href={linked} target="_blank" rel="noreferrer">
                      {t('plan.openLink')}
                    </a>
                  )}
                </div>
                {errors.link !== undefined && <p className="field-error">{errors.link}</p>}
              </div>
              <div className="booking-file">
                {all.map((one, at) => (
                  <span key={`${one.name}-${String(at)}`} className="booking-file-name">
                    <button
                      type="button"
                      className="link-button"
                      aria-pressed={one === file}
                      onClick={() => {
                        setLooked(at);
                      }}
                    >
                      📎 {one.name}
                    </button>
                    {/* One kept is taken away with the trip's documents, not here. */}
                    {at >= stored.length && (
                      <button
                        type="button"
                        className="icon-button icon-button-small"
                        aria-label={t('bookingForm.detach', { name: one.name })}
                        onClick={() => {
                          setFiles(files.filter((_, other) => other !== at - stored.length));
                          setLooked(0);
                        }}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
                {/* The file input itself, in its label: reached and opened as any field is. */}
                <label className="button button-small booking-file-pick">
                  {t('bookingForm.attach')}
                  <input
                    className="visually-hidden"
                    type="file"
                    multiple
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    onChange={(event) => {
                      setFiles([...files, ...(event.target.files ?? [])]);
                    }}
                  />
                </label>
              </div>
              {errors.day !== undefined && <p className="field-error">{errors.day}</p>}
              {failed && (
                <p className="form-failed" role="alert">
                  {t('plan.failed')}
                </p>
              )}
            </div>
            <div className="dialog-actions">
              {remove !== undefined && (
                <button
                  type="button"
                  className="button button-quiet dialog-action-apart"
                  onClick={() => {
                    setRemoving(true);
                  }}
                >
                  {t('bookingForm.remove')}
                </button>
              )}
              <button type="button" className="button" onClick={onClose}>
                {t('dialog.cancel')}
              </button>
              <button type="submit" className="button button-primary">
                {was ? t('bookingForm.save') : t('bookingForm.add')}
              </button>
            </div>
          </form>
          {preview !== null && file !== null && (
            <section
              className="booking-source"
              aria-label={t('bookingForm.source', { name: file.name })}
            >
              {file.type === 'application/pdf' ? (
                <>
                  {/* Where a text pressed goes, and where the last one went. */}
                  <p className="booking-source-into" aria-live="polite">
                    {took === null ? (
                      <>
                        {t('bookingForm.pressInto')}{' '}
                        <strong>{into ?? t('bookingForm.name')}</strong>
                      </>
                    ) : took === 'lost' ? (
                      <span className="booking-source-none">
                        {t('bookingForm.chooseField')}
                      </span>
                    ) : took.taken ? (
                      <>
                        {t('bookingForm.wentInto', { text: took.text })}{' '}
                        <strong>{took.into}</strong>
                      </>
                    ) : (
                      <span className="booking-source-none">
                        {t('bookingForm.noDayIn', { text: took.text, field: took.into })}
                      </span>
                    )}
                  </p>
                  <PdfView key={preview} url={preview} title={file.name} onPick={take} />
                </>
              ) : (
                <img className="booking-source-image" src={preview} alt={file.name} />
              )}
            </section>
          )}
        </div>
      </Dialog>
      {remove !== undefined && (
        <ConfirmDialog
          open={removing}
          title={t('bookingForm.removeQuestion', { name })}
          body={t('bookingForm.removeBody')}
          confirmLabel={t('bookingForm.removeConfirm')}
          onCancel={() => {
            setRemoving(false);
          }}
          onConfirm={() => {
            setRemoving(false);
            done(remove());
          }}
        />
      )}
    </>
  );
}
